import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { zoneLabel } from "@/lib/zones";
import { useLang } from "@/contexts/LangContext";
import { InjuryTimeline } from "@/components/InjuryTimeline";
import { PainAlerts } from "@/components/PainAlerts";
import { ReportCard } from "@/pages/Report";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ArrowLeft, Lock, Eye } from "lucide-react";

const L = {
  en: { back: "Back to club", daily: "Daily log (30 days)", medical: "Medical profile", stats: "Physical stats", report: "Weekly AI report", videos: "Video analyses", locked: "The player keeps this private.", noData: "No data yet", healed: "healed", sev: "sev",
    readiness: "Readiness", mental: "Mental", physical: "Physical", injury_risk: "Injury risk", noReport: "No report this week yet" },
  fr: { back: "Retour au club", daily: "Suivi quotidien (30 jours)", medical: "Profil médical", stats: "Stats physiques", report: "Rapport IA hebdo", videos: "Analyses vidéo", locked: "Le joueur garde cette section privée.", noData: "Pas encore de données", healed: "guéri", sev: "sév",
    readiness: "Fraîcheur", mental: "Mental", physical: "Physique", injury_risk: "Risque blessure", noReport: "Pas encore de rapport cette semaine" },
};
const RT = { fr: { patterns: "Tendances", risks: "Risques", wins: "Victoires", recos: "Recommandations", focus: "Focus semaine prochaine", readiness: "Fraîcheur", mental: "Mental", physical: "Physique", injury_risk: "Risque blessure", based: "Basé sur", current: "Cette semaine" },
  en: { patterns: "Patterns", risks: "Risks", wins: "Wins", recos: "Recommendations", focus: "Next week focus", readiness: "Readiness", mental: "Mental", physical: "Physical", injury_risk: "Injury risk", based: "Based on", current: "This week" } };

const Section = ({ title, locked, t, children, testid }) => (
  <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6" data-testid={testid}>
    <div className="font-display font-bold uppercase text-lg mb-3 flex items-center gap-2">{locked ? <Lock className="w-4 h-4 text-slate-500" /> : <Eye className="w-4 h-4 text-[#00F076]" />}{title}</div>
    {locked ? <div className="text-slate-500 text-sm italic">{t.locked}</div> : children}
  </div>
);

export default function ClubPlayer() {
  const { id } = useParams();
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get(`/club/players/${id}`).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Error")); }, [id]);

  if (err) return <div className="max-w-5xl mx-auto px-6 py-10 text-red-400" data-testid="club-player-error">{err}</div>;
  if (!d) return <div className="max-w-5xl mx-auto px-6 py-10 text-slate-400">Loading…</div>;
  const s = d.summary, p = s.privacy;
  const chart = [...(d.daily || [])].slice(0, 14).reverse().map((r) => ({ day: new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }), mood: r.mood, energy: r.energy, soreness: r.soreness, sleep: r.sleep_hours }));
  const report = (d.reports || [])[0];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="club-player-page">
      <Link to="/club" className="text-xs font-mono-tech uppercase tracking-wider text-slate-400 hover:text-white inline-flex items-center gap-1" data-testid="club-player-back"><ArrowLeft className="w-3 h-3" />{t.back}</Link>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400 uppercase">{s.position}</div>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-1" data-testid="club-player-name">{s.full_name}</h1>
          <div className="text-slate-500 text-xs mt-1">{s.email}</div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[["readiness", "#00F076"], ["mental", "#60a5fa"], ["physical", "#FFB800"], ["injury_risk", "#FF2E54"]].map(([k, c]) => (
            <div key={k} className="rounded-lg bg-[#121721] border border-[#1E2638] px-3 py-2 text-center min-w-[80px]" data-testid={`club-player-score-${k}`}>
              <div className="text-[9px] font-mono-tech uppercase text-slate-400">{t[k]}</div>
              <div className="font-display font-black text-2xl" style={{ color: c }}>{s.scores?.[k] ?? "—"}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6"><PainAlerts alerts={s.alerts} readOnly compact /></div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <Section title={t.daily} locked={!p.share_daily} t={t} testid="club-player-daily">
          {chart.length === 0 ? <div className="text-slate-500 text-sm">{t.noData}</div> : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chart}>
                <CartesianGrid stroke="#1E2638" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#94A3B8" fontSize={11} /><YAxis stroke="#94A3B8" fontSize={11} domain={[0, 10]} />
                <Tooltip contentStyle={{ background: "#090C10", border: "1px solid #1E2638", borderRadius: 8 }} /><Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="mood" stroke="#00F076" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="energy" stroke="#FFB800" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="soreness" stroke="#FF2E54" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="sleep" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
          {d.daily?.slice(0, 5).map((r) => (
            <div key={r.id} className="mt-2 text-xs font-mono-tech text-slate-400 flex flex-wrap gap-2 border-t border-[#1E2638] pt-1">
              <span>{new Date(r.created_at).toLocaleDateString()}</span><span className="uppercase">{r.session_type}</span>
              {r.pain_zones?.length > 0 && <span className="text-[#FF2E54]">{r.pain_zones.map((z) => zoneLabel(z, lang)).join(", ")}</span>}
              {r.notes && <span className="italic text-slate-500 truncate max-w-full">"{r.notes}"</span>}
            </div>
          ))}
        </Section>

        <Section title={t.stats} locked={!p.share_stats} t={t} testid="club-player-stats">
          {!d.stats?.length ? <div className="text-slate-500 text-sm">{t.noData}</div> : (
            <div className="grid grid-cols-3 gap-2">
              {[["weight_kg", "kg"], ["height_cm", "cm"], ["top_speed_kmh", "km/h"], ["endurance_vo2", "VO2"], ["muscle_index", "muscle"], ["explosive_power", "power"]].map(([k, u]) => (
                <div key={k} className="rounded-lg bg-[#090C10] border border-[#1E2638] p-3">
                  <div className="text-[9px] font-mono-tech uppercase text-slate-400">{u}</div>
                  <div className="font-display font-black text-xl">{d.stats[0][k] ?? "—"}</div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="mt-6">
        <Section title={t.medical} locked={!p.share_medical} t={t} testid="club-player-medical">
          <div className="grid md:grid-cols-2 gap-2 mb-4">
            {(d.medical || []).length === 0 && <div className="text-slate-500 text-sm">{t.noData}</div>}
            {(d.medical || []).map((m) => {
              const cur = m.current_severity ?? m.severity;
              return (
                <div key={m.id} className="rounded-lg bg-[#090C10] border border-[#1E2638] p-3" data-testid={`club-player-medical-${m.id}`}>
                  <div className="text-[10px] font-mono-tech uppercase text-slate-400">{zoneLabel(m.zone, lang)} · {m.kind} · <span className={cur === 0 ? "text-[#00F076]" : "text-amber-400"}>{cur === 0 ? t.healed : `${t.sev} ${cur}/5`}</span> · {new Date(m.created_at).toLocaleDateString()}</div>
                  <div className="font-display font-bold">{m.label}</div>
                  {m.ai?.overview && <div className="text-xs text-slate-400 mt-1 line-clamp-3">{m.ai.overview}</div>}
                </div>
              );
            })}
          </div>
          {d.timeline && <InjuryTimeline data={d.timeline} lang={lang} />}
        </Section>
      </div>

      <div className="mt-6">
        <Section title={t.report} locked={!p.share_reports} t={t} testid="club-player-report">
          {report ? <ReportCard doc={report} t={RT[lang] || RT.en} /> : <div className="text-slate-500 text-sm">{t.noReport}</div>}
        </Section>
      </div>

      <div className="mt-6">
        <Section title={t.videos} locked={!p.share_videos} t={t} testid="club-player-videos">
          {!d.videos?.length ? <div className="text-slate-500 text-sm">{t.noData}</div> : (
            <div className="grid md:grid-cols-2 gap-3">
              {d.videos.map((v) => (
                <div key={v.id} className="rounded-lg bg-[#090C10] border border-[#1E2638] p-3 flex gap-3">
                  {v.thumbnail && <img src={v.thumbnail} alt="" className="w-24 aspect-video object-cover rounded shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono-tech uppercase text-slate-400">{new Date(v.created_at).toLocaleDateString()} · {v.filename}</div>
                    <div className="text-sm font-semibold line-clamp-2">{v.analysis?.summary}</div>
                    {v.analysis?.score && <div className="text-[10px] font-mono-tech text-[#00F076] mt-1">{Object.entries(v.analysis.score).map(([k, val]) => `${k.replace("_", " ")} ${val}`).join(" · ")}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
